import { Divider, MenuItem, Select } from "@material-ui/core";
import { AddCircle, Cancel, RemoveCircle, Save } from "@material-ui/icons";
import { makeStyles } from "@material-ui/styles";
import { ChangeEvent, Fragment, ReactNode, useMemo, useState } from "react";
import { Button, useTranslate } from "react-admin";
import { useAppData } from "../../../../common/hooks/useAppData";
import { getDefaultLayoutProps } from "../common";

interface Props {
  selectedAttributes: string[];
  onChange: (attributes?: string[]) => void;
  widgetHeight?: number;
}

const useSelectStyles = makeStyles(() => ({
  font: {
    fontSize: "0.875rem",
  },
}));

const attributesPerRow = 4;
const getMaxAttributesCount = (widgetHeight?: number) => {
  const rowHeight = getDefaultLayoutProps().rowHeight;
  widgetHeight = widgetHeight ?? rowHeight * 2 + 10;
  const usableHeight = widgetHeight - 10;
  const rows = usableHeight / rowHeight;
  return Math.floor(rows * attributesPerRow);
};

interface SelectAttributeProps {
  attributes: { value: string; label: string }[];
  onChange: (
    event: ChangeEvent<{
      name?: string | undefined;
      value: unknown;
    }>,
    child: ReactNode
  ) => void;
  value?: string;
}

const SelectAttribute = ({
  attributes,
  value,
  onChange,
}: SelectAttributeProps) => {
  const classes = useSelectStyles();
  return (
    <Select
      className={classes.font}
      value={value}
      margin="dense"
      variant="outlined"
      onChange={onChange}
    >
      {attributes.map((a) => (
        <MenuItem key={a.value} value={a.value}>
          {a.label ?? "-"}
        </MenuItem>
      ))}
    </Select>
  );
};

function getOrderedArray<T, TValue>(
  array: T[],
  selector: (element: T) => TValue,
  desc: boolean = false
) {
  return [...array].sort((a, b) => {
    const left = selector(a);
    const right = selector(b);
    if (left === right) return 0;
    return (desc ? left > right : left < right) ? -1 : 1;
  });
}

export const VehicleSensorInfoWidgetEditor = (props: Props) => {
  const attributes = useAppData((x) => x.sensorAttributes);
  const translate = useTranslate();
  const orderedAttributes = useMemo(() => {
    const translatedAndParsed = (attributes ?? []).map((x) => ({
      value: x,
      label: translate(`resources.sensorAttributes.${x}`, {
        _: x,
      }),
    }));

    const ordered = getOrderedArray(translatedAndParsed, (x) => x.label);
    return ordered;
  }, [attributes, translate]);

  const [selectedAttributes, setSelectedAttributes] = useState<string[]>(
    props.selectedAttributes
  );
  return (
    <div className="info-wrapper info-wrapper-3-2">
      {selectedAttributes.map((a) => (
        <Fragment key={a}>
          <div className="info-label">
            <SelectAttribute
              value={a}
              attributes={orderedAttributes.filter(
                (x) => !selectedAttributes.includes(x.value) || x.value === a
              )}
              onChange={(e) => {
                setSelectedAttributes((x) => [
                  ...x.filter((current) => current !== a),
                  e.target.value as string,
                ]);
              }}
            />
          </div>
          <div className="info-value">
            <Button
              label="Remove"
              onClick={() =>
                setSelectedAttributes((x) => [
                  ...x.filter((current) => current !== a),
                ])
              }
            >
              <RemoveCircle />
            </Button>
          </div>
        </Fragment>
      ))}
      <Divider className="info-row" />
      <div className="info-row">
        <Button
          label="Add new sensor value"
          disabled={
            selectedAttributes.length >=
              getMaxAttributesCount(props.widgetHeight) ||
            selectedAttributes.includes("")
          }
          onClick={() => setSelectedAttributes((x) => [...x, ""])}
        >
          <AddCircle />
        </Button>
        <Button label="OK" onClick={() => props.onChange(selectedAttributes)}>
          <Save />
        </Button>
        <Button label="Cancel" onClick={() => props.onChange()}>
          <Cancel />
        </Button>
      </div>
    </div>
  );
};
